/** Пригласить друзей: реферальная ссылка на бота, счётчики и приглашённые за неделю. */
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { api, errorMessage } from '../lib/api';
import { ErrorState, Loading, OfflineBanner } from '../components/States';
import * as telegram from '../lib/telegram';
import { useActions } from '../state/actions';
import { useStore } from '../state/store';
import { formatDate, locale, t } from '../i18n';

const stars = (quarters: number) => `${(quarters / 4).toLocaleString(locale(), { maximumFractionDigits: 2 })} ⭐`;
const withdrawalStatus = (status: 'pending' | 'processing' | 'paid' | 'rejected'): string => t(`invite.status.${status}`);
const weekday = (day: number): string => t(`invite.weekdays.${day}` as 'invite.weekdays.0');

/** Подписи столбиков: от шести дней назад до сегодня. */
function dayLabels(count: number): string[] {
  const today = new Date();
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() - (count - 1 - index));
    return weekday(date.getDay());
  });
}

/** Буфер обмена: сначала современный API, в старых WebView запасной путь через textarea. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // ниже запасной путь
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    return ok;
  } catch {
    return false;
  }
}

function WeekChart({ byDay }: { byDay: number[] }): ReactElement {
  const max = Math.max(...byDay, 1);
  const labels = dayLabels(byDay.length);
  return (
    <div className="bars" role="img" aria-label={t('invite.chart.aria')}>
      {byDay.map((count, index) => (
        <div key={labels[index] ?? index} className="bar-col">
          {count > 0 ? <span className="bar-count">{count}</span> : null}
          <span
            className={count > 0 ? 'bar-fill' : 'bar-fill zero'}
            style={{ height: count > 0 ? Math.max(8, Math.round((count / max) * 56)) : 4 }}
          />
          <span className="bar-day">{labels[index]}</span>
        </div>
      ))}
    </div>
  );
}

export function Invite(): ReactElement {
  const { state } = useStore();
  const actions = useActions();
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawalError, setWithdrawalError] = useState('');

  // Счётчики растут, пока друзья приходят: запрашиваем при каждом открытии экрана.
  const requested = useRef(false);
  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    void actions.loadProfile();
  }, [actions]);

  const referral = state.profile?.referral ?? null;
  const link = referral?.link ?? null;
  const weekTotal = referral ? referral.invitedByDay.reduce((sum, count) => sum + count, 0) : 0;

  const requestWithdrawal = async (): Promise<void> => {
    setWithdrawing(true);
    setWithdrawalError('');
    try {
      await api.requestReferralWithdrawal();
      await actions.loadProfile();
      actions.toast(t('invite.toast.withdrawal'));
    } catch (error) {
      setWithdrawalError(errorMessage(error));
    } finally {
      setWithdrawing(false);
    }
  };

  const share = (): void => {
    if (!link) return;
    telegram.hapticFeedback.impact('light');
    // Стандартный диалог Telegram «поделиться»: внутри клиента открывается как t.me-ссылка.
    telegram.openLink(
      `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(t('invite.share.text'))}`,
    );
  };

  const copy = async (): Promise<void> => {
    if (!link) return;
    const ok = await copyText(link);
    telegram.hapticFeedback.notification(ok ? 'success' : 'error');
    actions.toast(ok ? t('invite.toast.copied') : t('invite.toast.copyFailed'));
  };

  return (
    <>
      <h1>{t('invite.title')}</h1>
      <p className="lead">{t('invite.lead')}</p>
      {!state.online ? <OfflineBanner /> : null}

      {state.profileError ? (
        <ErrorState message={state.profileError} onRetry={() => void actions.loadProfile()} />
      ) : null}
      {!referral && !state.profileError ? <Loading rows={2} /> : null}

      {referral ? (
        <>
          <div className="card">
            <span className="label">{t('invite.link.label')}</span>
            {link ? (
              <span className="invite-link">{link}</span>
            ) : (
              <span className="small muted">
                {t('invite.link.missing')}
              </span>
            )}
            <div className="btn-row">
              <button type="button" className="btn primary grow" disabled={!link} onClick={share}>
                {t('invite.share')}
              </button>
              <button type="button" className="btn grow" disabled={!link} onClick={() => void copy()}>
                {t('invite.copy')}
              </button>
            </div>
          </div>

          <div className="stats">
            <div className="card stat">
              <span className="fact-label">{t('invite.stat.invited')}</span>
              <span className="fact-value">{referral.invited}</span>
            </div>
            <div className="card stat">
              <span className="fact-label">{t('invite.stat.commission')}</span>
              <span className="fact-value">25%</span>
            </div>
          </div>

          <div className="card">
            <span className="card-title">{t('invite.withdraw.title')}</span>
            <div className="facts referral-facts">
              <div className="fact">
                <span className="fact-label">{t('invite.withdraw.available')}</span>
                <span className="fact-value">{stars(referral.availableQuarterStars)}</span>
              </div>
              <div className="fact">
                <span className="fact-label">{t('invite.withdraw.paid')}</span>
                <span className="fact-value">{stars(referral.paidQuarterStars)}</span>
              </div>
            </div>
            <p className="small muted">{t('invite.withdraw.info', { min: stars(referral.minimumWithdrawalStars * 4) })}</p>
            {referral.reservedQuarterStars > 0 ? <p className="small">{t('invite.withdraw.reserved', { amount: stars(referral.reservedQuarterStars) })}</p> : null}
            {referral.debtQuarterStars > 0 ? <p className="small">{t('invite.withdraw.debt', { amount: stars(referral.debtQuarterStars) })}</p> : null}
            <div className="btn-col">
            <button type="button" className="btn" disabled={withdrawing || !state.online || referral.availableQuarterStars < referral.minimumWithdrawalStars * 4 || referral.withdrawals.some(w => w.status === 'pending' || w.status === 'processing')} onClick={() => void requestWithdrawal()}>
              {withdrawing ? t('invite.withdraw.sending') : t('invite.withdraw.request')}
            </button>
            {state.profile?.links.support ? <button type="button" className="btn" onClick={() => telegram.openLink(state.profile!.links.support!)}>{t('invite.withdraw.support')}</button> : null}
            </div>
            {withdrawalError ? <p role="alert">{withdrawalError}</p> : null}
            {referral.withdrawals.map(w => <div key={w.id} className="section-title">
              <span>{stars(w.amount_quarter_stars)} — {withdrawalStatus(w.status)}</span>
              <p className="small muted">{formatDate(w.created_at)} · {t('invite.withdraw.number', { id: w.id.slice(0, 8) })}</p>
              {w.note ? <p className="small">{w.note}</p> : null}
            </div>)}
          </div>

          <div className="card">
            <span className="label">{t('invite.week.title')}</span>
            {weekTotal > 0 ? (
              <WeekChart byDay={referral.invitedByDay} />
            ) : (
              <span className="muted">{t('invite.week.empty')}</span>
            )}
          </div>

          <span className="small muted section-title">{t('invite.footnote')}</span>
        </>
      ) : null}
    </>
  );
}
